import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import ParticlesBg from './ParticlesBg';
import axios from 'axios';
import { API_BASE } from '../api';
import { useAuth } from '../context/AuthContext';
import '../App.css';

const PER_PAGE = 25;

// ── Reward items per achievement key (mirrors AchievementsPage.js) ───────
const ACHIEVEMENT_REWARDS = {
  on_fire_4:          [{ type: 'particles', label: 'Fire',                  icon: 'fa-fire'        }, { type: 'badge', label: 'Fire Lord Badge',        icon: 'fa-certificate' }],
  flame_picker_4:     [{ type: 'particles', label: 'Stars',                 icon: 'fa-star'        }, { type: 'badge', label: 'Legend Badge',           icon: 'fa-certificate' }],
  login_novice:       [{ type: 'card',   label: 'Card Color',               icon: 'fa-th-large'    }],
  login_amateur:      [{ type: 'card',   label: 'Card Animations',          icon: 'fa-th-large'    }],
  wins_100:           [{ type: 'card',   label: 'Neon Card Effect',         icon: 'fa-th-large'    }, { type: 'name', label: 'Neon Name Effect', icon: 'fa-font' }, { type: 'border', label: 'Neon Border', icon: 'fa-circle-o' }, { type: 'banner', label: 'Neon Banner', icon: 'fa-picture-o' }],
  leaderboard_top1:   [{ type: 'card',   label: 'Gold Shimmer Card',        icon: 'fa-th-large'    }, { type: 'name', label: 'Gold Name Effect', icon: 'fa-font' }, { type: 'banner', label: 'Gold Banner', icon: 'fa-picture-o' }, { type: 'border', label: 'Gold Border', icon: 'fa-circle-o' }, { type: 'badge', label: 'GOAT Badge', icon: 'fa-certificate' }],
  flame_picker_2:     [{ type: 'name',   label: 'Name Color',               icon: 'fa-font'        }],
  flame_picker_3:     [{ type: 'name',   label: 'Name Animations',          icon: 'fa-font'        }],
  leaderboard_top10:  [{ type: 'name',   label: 'Glimmer Name Effect',      icon: 'fa-font'        }, { type: 'badge', label: 'Elite Badge', icon: 'fa-certificate' }],
  login_legend:       [{ type: 'tagline', label: 'Tagline',                 icon: 'fa-quote-left'  }, { type: 'badge', label: 'Legend Badge', icon: 'fa-certificate' }],
  login_professional: [{ type: 'banner', label: 'Custom Banner',            icon: 'fa-picture-o'   }],
  on_fire_3:          [{ type: 'banner', label: 'Heat Banner',              icon: 'fa-picture-o'   }, { type: 'badge', label: 'Flame Prince Badge', icon: 'fa-certificate' }],
  wins_10:            [{ type: 'border', label: 'Border Color',             icon: 'fa-circle-o'    }],
  wins_50:            [{ type: 'border', label: 'Border Animations',        icon: 'fa-circle-o'    }],
  on_fire_2:          [{ type: 'border', label: 'Ember Border',             icon: 'fa-circle-o'    }],
  social_2:           [{ type: 'badge',  label: 'Friendly Badge',           icon: 'fa-certificate' }],
  social_4:           [{ type: 'badge',  label: 'Popular Badge',            icon: 'fa-certificate' }],
  perfect_day:        [{ type: 'badge',  label: 'Perfectionist Badge',      icon: 'fa-certificate' }],
  loss_streak_10:     [{ type: 'badge',  label: '💩 Badge',                 icon: 'fa-certificate' }],
  all_leagues:        [{ type: 'badge',  label: 'All-Rounder Badge',        icon: 'fa-certificate' }],
  betrayal:           [{ type: 'badge',  label: 'Traitor Badge',            icon: 'fa-certificate' }],
  classic_mode:       [{ type: 'badge',  label: 'Nostalgic Badge',          icon: 'fa-certificate' }],
  cis4914:            [{ type: 'badge',  label: 'Founder Badge',            icon: 'fa-certificate' }],
};

// Visual display mapping for cosmetic reward items → ap-pill class + label
const REWARD_DISPLAY_MAP = {
  'card:Card Color':            { cls: '',                      label: 'Card Colors'       },
  'card:Card Animations':       { cls: '',                      label: 'Card Animations'   },
  'card:Neon Card Effect':      { cls: 'ap-pill--neon',         label: 'RGB'               },
  'card:Gold Shimmer Card':     { cls: 'ap-pill--gold-shimmer', label: 'Gold Card'         },
  'name:Name Color':            { cls: '',                      label: 'Name Colors'       },
  'name:Name Animations':       { cls: '',                      label: 'Name Animations'   },
  'name:Glimmer Name Effect':   { cls: 'ap-pill--glimmer',      label: 'Glimmer'           },
  'name:Neon Name Effect':      { cls: 'ap-pill--neon',         label: 'RGB'               },
  'name:Gold Name Effect':      { cls: 'ap-pill--gold',         label: 'Gold'              },
  'banner:Custom Banner':       { cls: '',                      label: 'Custom Banner'     },
  'banner:Heat Banner':         { cls: 'ap-pill--heat',         label: 'Heat'              },
  'banner:Neon Banner':         { cls: 'ap-pill--cyber',        label: 'RGB'               },
  'banner:Gold Banner':         { cls: 'ap-pill--gold',         label: 'Gold'              },
  'border:Border Color':        { cls: '',                      label: 'Border Colors'     },
  'border:Border Animations':   { cls: '',                      label: 'Border Animations' },
  'border:Ember Border':        { cls: 'ap-pill--ember',        label: 'Ember'             },
  'border:Neon Border':         { cls: 'ap-pill--neon',         label: 'RGB'               },
  'border:Gold Border':         { cls: 'ap-pill--gold',         label: 'Gold'              },
  'tagline:Tagline':            { cls: '',                      label: 'Tagline'           },
  'particles:Fire':   { cls: 'ap-pill--heat',  label: 'Fire'  },
  'particles:Stars':  { cls: 'ap-pill--stars', label: 'Stars' },
};

// Badge label → CSS class mapping
const BADGE_DISPLAY_MAP = {
  'Fire Lord Badge':     { cls: 'badge-fire-lord',    label: 'Fire Lord'    },
  'Legend Badge':        { cls: 'badge-legend',        label: 'Legend'       },
  'GOAT Badge':          { cls: 'badge-goat',          label: 'GOAT'         },
  'Elite Badge':         { cls: 'badge-elite',         label: 'Elite'        },
  'Flame Prince Badge':  { cls: 'badge-flame-prince',  label: 'Flame Prince' },
  'Friendly Badge':      { cls: 'badge-friendly',      label: 'Friendly'     },
  'Popular Badge':       { cls: 'badge-popular',       label: 'Popular'      },
  'Perfectionist Badge': { cls: 'badge-perfectionist', label: 'Perfectionist'},
  '💩 Badge':            { cls: 'badge-poop',          label: '💩'           },
  'All-Rounder Badge':   { cls: 'badge-all-rounder',   label: 'All-Rounder'  },
  'Traitor Badge':       { cls: 'badge-traitor',       label: 'Traitor'      },
  'Nostalgic Badge':     { cls: 'badge-nostalgic',     label: 'Nostalgic'    },
  'Founder Badge':       { cls: 'badge-info',          label: 'Founder'      },
};

// Achievements that unlock a full themed cosmetic set — collapse all cosmetics into one row
const SET_REWARDS = {
  wins_100:         { cls: 'ap-pill--neon',         label: 'RGB'  },
  leaderboard_top1: { cls: 'ap-pill--gold-shimmer',  label: 'Gold' },
};

// Defines what each element of a set preview actually applies
const SET_PREVIEW_CONFIG = {
  wins_100: {
    card:   { label: 'Card Effect', specialValue: 'neon'         },
    name:   { label: 'Name Effect', specialValue: 'neon'         },
    banner: { label: 'Banner',      specialValue: 'neon'         },
    border: { label: 'Border',      specialValue: 'neon'         },
  },
  leaderboard_top1: {
    card:   { label: 'Card Effect', specialValue: 'gold-shimmer' },
    name:   { label: 'Name Effect', specialValue: 'gold'         },
    banner: { label: 'Banner',      specialValue: 'gold'         },
    border: { label: 'Border',      specialValue: 'gold'         },
  },
};

// Row label shown above each individual cosmetic reward type
const REWARD_ROW_LABELS = {
  card:      'Card Effect',
  name:      'Name Effect',
  banner:    'Banner',
  border:    'PFP Border',
  tagline:   'Tagline',
  particles: 'Particles',
};

// Badge key → { label, cls } (mirrors ProfilePage.js BADGE_META)
const BADGE_META = {
  admin:         { label: 'Admin',            cls: 'badge-admin'        },
  founder:       { label: 'Founder',          cls: 'badge-info'         },
  '1st':         { label: 'Leaderboard: 1st', cls: 'badge-gold'         },
  '2nd':         { label: 'Leaderboard: 2nd', cls: 'badge-silver'       },
  '3rd':         { label: 'Leaderboard: 3rd', cls: 'badge-bronze'       },
  fire_lord:     { label: 'Fire Lord',        cls: 'badge-fire-lord'    },
  goat:          { label: 'GOAT',             cls: 'badge-goat'         },
  legend:        { label: 'Legend',           cls: 'badge-legend'       },
  elite:         { label: 'Elite',            cls: 'badge-elite'        },
  flame_prince:  { label: 'Flame Prince',     cls: 'badge-flame-prince' },
  popular:       { label: 'Popular',          cls: 'badge-popular'      },
  perfectionist: { label: 'Perfectionist',    cls: 'badge-perfectionist'},
  poop:          { label: '\ud83d\udca9',      cls: 'badge-poop'         },
  all_rounder:   { label: 'All-Rounder',      cls: 'badge-all-rounder'  },
  traitor:       { label: 'Traitor',          cls: 'badge-traitor'      },
  friendly:      { label: 'Friendly',         cls: 'badge-friendly'     },
  nostalgic:     { label: 'Nostalgic',        cls: 'badge-nostalgic'    },
};

// Returns true for reward types we support previewing
const isPreviewable = (r) => {
  if (r.type === 'badge' && BADGE_DISPLAY_MAP[r.label]) return true;
  if (r.type === 'banner' && (r.label === 'Heat Banner' || r.label === 'Custom Banner')) return true;
  if (r.type === 'tagline') return true;
  if (r.type === 'particles') return true;
  if (r.type === 'name') return true;
  if (r.type === 'border') return true;
  if (r.type === 'card' && (r.label === 'Card Color' || r.label === 'Card Animations')) return true;
  return false;
};

// ── Mini profile card rendered inside the preview modal ─────────────────
const ProfileMiniCard = ({ user, reward, setPreviewKey, setToggles, customPreview, particlesPreset }) => {
  const c = user.customization || {};

  let bannerSpecial  = c.banner_special || 'none';
  let cardSpecial    = c.card_special   || 'none';
  let cardAnimMode   = c.card_anim      || 'static';
  let borderSpecial  = c.border_special || 'none';
  let borderColor1   = c.border_color1  || '';
  let borderColor2   = c.border_color2  || '';
  let borderAnimMode = c.border_anim    || 'static';
  let nameSpecial    = c.name_special   || 'default';
  let nameColor1     = c.name_color1    || '#e05c00';
  let nameColor2     = c.name_color2    || '#ffd200';
  let nameAnimMode   = c.name_anim      || 'static';
  let tagline        = c.tagline        || '';
  let previewBadge   = null;
  let cardPreviewColors = {};

  if (reward) {
    if (reward.type === 'banner' && reward.label === 'Heat Banner') {
      bannerSpecial = 'heat';
    } else if (reward.type === 'name' && reward.label === 'Glimmer Name Effect') {
      nameSpecial = 'glimmer';
    } else if (reward.type === 'border' && reward.label === 'Ember Border') {
      borderSpecial = 'ember';
    } else if (reward.type === 'tagline') {
      tagline = tagline || 'Example Tagline.';
    } else if (reward.type === 'badge') {
      const bdg = BADGE_DISPLAY_MAP[reward.label];
      if (bdg) previewBadge = bdg;
    }
  }

  // Apply set overrides based on toggle state
  if (setPreviewKey && setToggles && SET_PREVIEW_CONFIG[setPreviewKey]) {
    const cfg = SET_PREVIEW_CONFIG[setPreviewKey];
    if (setToggles.card   && cfg.card)   cardSpecial   = cfg.card.specialValue;
    if (setToggles.name   && cfg.name)   nameSpecial   = cfg.name.specialValue;
    if (setToggles.banner && cfg.banner) bannerSpecial = cfg.banner.specialValue;
    if (setToggles.border && cfg.border) borderSpecial = cfg.border.specialValue;
  }

  // Apply custom color/animation preview overrides
  if (reward && customPreview) {
    if (reward.type === 'banner' && reward.label === 'Custom Banner') {
      bannerSpecial = '__custom_preview__'; // sentinel handled below
    } else if (reward.type === 'card' && (reward.label === 'Card Color' || reward.label === 'Card Animations')) {
      cardSpecial = 'none';
      cardAnimMode = reward.label === 'Card Animations' ? customPreview.animMode : 'static';
      cardPreviewColors = { '--card-c1': customPreview.color1, '--card-c2': customPreview.color2 };
    } else if (reward.type === 'name' && (reward.label === 'Name Color' || reward.label === 'Name Animations')) {
      nameSpecial = 'default';
      nameAnimMode = reward.label === 'Name Animations' ? customPreview.animMode : 'static';
      nameColor1 = customPreview.color1;
      nameColor2 = customPreview.color2;
    } else if (reward.type === 'border' && (reward.label === 'Border Color' || reward.label === 'Border Animations')) {
      borderSpecial = 'none';
      borderAnimMode = reward.label === 'Border Animations' ? customPreview.animMode : 'static';
      borderColor1 = customPreview.color1;
      if (borderAnimMode === 'gradient') borderColor2 = customPreview.color2;
    }
  }

  const isCustomBannerPreview = bannerSpecial === '__custom_preview__';
  const bannerCls = isCustomBannerPreview
    ? ' ap-banner--custom'
    : bannerSpecial !== 'none'
    ? ` ap-banner--${bannerSpecial}`
    : c.banner_color1 ? ' ap-banner--custom' : '';
  const isCustomCardPreview = !!(reward && customPreview && reward.type === 'card');
  const isCustomNamePreview = !!(reward && customPreview && reward.type === 'name');
  const cardCls = cardSpecial !== 'none'
    ? ` ap-header-card--${cardSpecial}`
    : (isCustomCardPreview || cardAnimMode !== 'static') ? ` ap-header-card--custom-${cardAnimMode}` : '';
  const bannerStyle = (bannerSpecial === 'none' || isCustomBannerPreview)
    ? {
        position: 'relative', overflow: 'hidden',
        '--banner-c1': isCustomBannerPreview ? customPreview.color1 : (c.banner_color1 || ''),
        '--banner-c2': isCustomBannerPreview ? customPreview.color2 : (c.banner_color2 || ''),
        ...cardPreviewColors,
      }
    : { position: 'relative', overflow: 'hidden', ...cardPreviewColors };
  const ringCls = borderSpecial !== 'none'
    ? ` ap-avatar-ring--${borderSpecial}`
    : borderColor1 ? ` ap-avatar-ring--custom-${borderAnimMode}` : '';
  const ringStyle = borderSpecial === 'none'
    ? {
        '--ring-c1': borderColor1,
        ...(borderAnimMode === 'gradient' ? { '--ring-c2': borderColor2 } : {}),
      }
    : {};
  const nameCls = nameSpecial !== 'default'
    ? ` ap-hero-name--${nameSpecial}`
    : (isCustomNamePreview || nameAnimMode !== 'static') ? ` ap-hero-name--custom-${nameAnimMode}` : '';
  const nameStyle = nameSpecial === 'default'
    ? { '--name-c1': nameColor1, '--name-c2': nameColor2 }
    : {};

  const existingBadgeItems = (user.badges || [])
    .map(key => BADGE_META[key])
    .filter(Boolean);

  let displayBadges;
  if (previewBadge) {
    if (existingBadgeItems.length >= 3) {
      displayBadges = [...existingBadgeItems.slice(0, 2), { ...previewBadge, isPreview: true }];
    } else {
      displayBadges = [...existingBadgeItems, { ...previewBadge, isPreview: true }];
    }
  } else {
    displayBadges = existingBadgeItems;
  }

  const wins    = user.score  ?? 0;
  const losses  = user.losses ?? 0;
  const total   = wins + losses;
  const winRate = total > 0 ? Math.round((wins / total) * 100) : null;

  return (
    <div className={`ap-header-card${bannerCls}${cardCls} achp-preview-card`} style={bannerStyle}>
      {bannerSpecial === 'heat' && [
        { left: '5%',  w: '14%', skew: -12, delay: '0s',    dur: '3.4s'  },
        { left: '16%', w: '10%', skew:   6, delay: '0.65s', dur: '4.0s'  },
        { left: '27%', w: '17%', skew:  -7, delay: '1.25s', dur: '3.1s'  },
        { left: '40%', w: '11%', skew:  10, delay: '0.2s',  dur: '3.8s'  },
        { left: '52%', w: '19%', skew:  -5, delay: '1.75s', dur: '2.85s' },
        { left: '64%', w: '12%', skew:   8, delay: '0.9s',  dur: '4.15s' },
        { left: '76%', w: '15%', skew:  -9, delay: '1.5s',  dur: '3.55s' },
        { left: '87%', w:  '9%', skew:   4, delay: '0.4s',  dur: '3.25s' },
      ].map((f, i) => (
        <div key={i} className="ap-heat-flame" style={{ left: f.left, width: f.w, transform: `translateX(-50%) skewX(${f.skew}deg)`, animationDelay: f.delay, animationDuration: f.dur }} />
      ))}
      {/* Fire particles inside card (matches actual profile behaviour) */}
      {particlesPreset && particlesPreset !== 'none' && particlesPreset !== 'stars' && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 9, pointerEvents: 'none', borderRadius: 'inherit', overflow: 'hidden' }}>
          <ParticlesBg preset={particlesPreset} />
        </div>
      )}
      <div className="ap-header-card-inner">
        <div className={`ap-avatar-ring${ringCls}`} style={ringStyle}>
          <img
            className="ap-avatar"
            src={user.profilePicture || '/default.jpg'}
            alt={user.firstName}
            onError={e => { e.target.src = '/default.jpg'; }}
          />
        </div>
        <div className="ap-hero-info">
          <p className={`ap-hero-name${nameCls}`} style={nameStyle}>
            {user.firstName} {user.lastName}
          </p>
          {tagline && <p className="ap-tagline">{tagline}</p>}
          {user.username && <p className="ap-hero-username">@{user.username}</p>}
          {displayBadges.length > 0 && (
            <div className="ap-badges">
              {displayBadges.map((bdg, i) => (
                <span key={i} className={`badge ${bdg.cls}`}>
                  {bdg.label}
                </span>
              ))}
            </div>
          )}
          <div className="ap-stats-row">
            <div className="ap-stat-tile">
              <div className="ap-stat-value green">{wins}</div>
              <div className="ap-stat-label">Wins</div>
            </div>
            <div className="ap-stat-tile">
              <div className="ap-stat-value red">{losses}</div>
              <div className="ap-stat-label">Losses</div>
            </div>
            <div className="ap-stat-tile ap-stat-tile--winrate">
              <div className="ap-stat-value">{winRate !== null ? `${winRate}%` : '\u2014'}</div>
              <div className="ap-stat-label">Win Rate</div>
              {winRate !== null && (
                <div className="ap-winrate-bar-track">
                  <div
                    className="ap-winrate-bar-fill"
                    style={{
                      width: `${winRate}%`,
                      background: winRate >= 60 ? 'var(--green)' : winRate >= 40 ? '#f59e0b' : 'var(--red)'
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AchievementPage = () => {
  const { achievementKey } = useParams();
  const navigate = useNavigate();
  const { authData } = useAuth();

  const [achievement, setAchievement] = useState(null);
  const [earners, setEarners]         = useState([]);
  const [totalEarners, setTotalEarners] = useState(0);
  const [page, setPage]   = useState(1);
  const [loading, setLoading]   = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState('');

  // Preview state
  const [previewOpen, setPreviewOpen]     = useState(false);
  const [previewReward, setPreviewReward] = useState(null);
  const [previewUser, setPreviewUser]     = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError]   = useState('');
  const [setPreviewKey, setSetPreviewKey] = useState(null);
  const [setToggles, setSetToggles]       = useState({ card: true, name: true, banner: true, border: true });
  const [customPreview, setCustomPreview] = useState({ color1: '#e05c00', color2: '#ffd200', animMode: 'pulse' });
  const [rewardsOpen, setRewardsOpen]     = useState(false);
  const [allAchievements, setAllAchievements] = useState([]);

  const fetchData = useCallback(async (p) => {
    p === 1 ? setLoading(true) : setPageLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/achievements/${achievementKey}`, {
        params:  { page: p, per_page: PER_PAGE },
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      setAchievement({
        key: d.key, title: d.title, description: d.description,
        category: d.category, badge_icon: d.badge_icon,
        reward_type: d.reward_type, reward_xp: d.reward_xp,
        reward_description: d.reward_description,
        progress_total: d.progress_total, user_progress: d.user_progress,
        earned: d.earned, earned_at: d.earned_at,
        global_earned_count: d.global_earned_count,
        global_earned_pct: d.global_earned_pct,
        expires_at: d.expires_at,
      });
      setEarners(d.earners || []);
      setTotalEarners(d.total_earners || 0);
    } catch (err) {
      if (err.response?.status === 404) setError('Achievement not found.');
      else setError('Failed to load achievement.');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }, [achievementKey]);

  useEffect(() => {
    document.body.classList.add('achievements-page-bg');
    fetchData(1);
    // Fetch full list for prev/next navigation
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/achievements/all`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const CATEGORY_ORDER = ['progression', 'first_time', 'unobtainable'];
      const sorted = (res.data.achievements || []).slice().sort((a, b) => {
        const ci = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
        return ci !== 0 ? ci : 0;
      });
      setAllAchievements(sorted);
    }).catch(() => {});
    return () => document.body.classList.remove('achievements-page-bg');
  }, [fetchData]);

  const handlePage = (p) => {
    setPage(p);
    fetchData(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const CUSTOM_REWARD_LABELS = ['Card Color', 'Card Animations', 'Name Color', 'Name Animations', 'Border Color', 'Border Animations'];
  const openPreview = async (rewardItem) => {
    setPreviewReward(rewardItem);
    setPreviewOpen(true);
    if (rewardItem.type === 'set') {
      setSetPreviewKey(rewardItem.key);
      setSetToggles({ card: true, name: true, banner: true, border: true });
    } else {
      setSetPreviewKey(null);
    }
    if (CUSTOM_REWARD_LABELS.includes(rewardItem.label)) {
      setCustomPreview({ color1: '#e05c00', color2: '#ffd200', animMode: 'pulse' });
    }
    if (previewUser) return; // already cached
    const userId = authData?.user?.id;
    if (!userId) return;
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const token = localStorage.getItem('token');
      const [userResp, customResp] = await Promise.all([
        axios.get(`${API_BASE}/data/get-user/${userId}`),
        axios.get(`${API_BASE}/user/profile-customization`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      const u = userResp.data;
      setPreviewUser({
        firstName: u.firstName,
        lastName: u.lastName,
        username: u.username,
        profilePicture: u.profile_picture,
        score: u.score ?? 0,
        losses: u.losses ?? 0,
        badges: u.badges || [],
        customization: customResp.data.customization || {},
      });
    } catch {
      setPreviewError('Could not load your profile data for preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewReward(null);
    setSetPreviewKey(null);
  };

  const totalPages = Math.ceil(totalEarners / PER_PAGE);

  const CATEGORY_LABELS = {
    progression: 'Progression',
    first_time: 'First Time',
    unobtainable: 'Unobtainable',
  };

  if (loading) return (
    <BaseLayout>
      <FlameLoader text="Loading achievement..." />
    </BaseLayout>
  );

  if (error) return (
    <BaseLayout>
      <div className="ach-page" style={{ paddingTop: 48 }}>
        <p className="error-text">{error}</p>
        <button className="fp-btn fp-btn-outline" onClick={() => navigate('/achievements')}>
          Back to Achievements
        </button>
      </div>
    </BaseLayout>
  );

  if (!achievement) return null;

  const pct = achievement.progress_total > 1
    ? Math.min(Math.round((achievement.user_progress / achievement.progress_total) * 100), 100)
    : (achievement.earned ? 100 : 0);

  const earnedDate = achievement.earned_at
    ? new Date(achievement.earned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const expiryDate = achievement.expires_at
    ? new Date(achievement.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const hasExpired = achievement.expires_at && new Date(achievement.expires_at) < new Date();

  const currentIdx  = allAchievements.findIndex(a => a.key === achievementKey);
  const prevAch     = currentIdx > 0 ? allAchievements[currentIdx - 1] : null;
  const nextAch     = currentIdx >= 0 && currentIdx < allAchievements.length - 1 ? allAchievements[currentIdx + 1] : null;

  return (
    <BaseLayout>
      <div className="ach-page">

        {/* Prev / Next navigation */}
        {allAchievements.length > 0 && (
          <div className="achp-sibling-nav">
            {prevAch ? (
              <button className="achp-sibling-btn" onClick={() => navigate(`/achievements/${prevAch.key}`)}>
                <i className="fa fa-chevron-left" />
                <span className="achp-sibling-label">
                  <span className="achp-sibling-hint">Previous</span>
                  <span className="achp-sibling-title">{prevAch.title}</span>
                </span>
              </button>
            ) : <span />}
            {nextAch ? (
              <button className="achp-sibling-btn achp-sibling-btn--right" onClick={() => navigate(`/achievements/${nextAch.key}`)}>
                <span className="achp-sibling-label achp-sibling-label--right">
                  <span className="achp-sibling-hint">Next</span>
                  <span className="achp-sibling-title">{nextAch.title}</span>
                </span>
                <i className="fa fa-chevron-right" />
              </button>
            ) : <span />}
          </div>
        )}

        {/* Hero */}
        <div className={`achp-hero ${achievement.earned ? 'achp-hero--earned' : ''}`}>
          <div className={`achp-hero-icon ${achievement.earned ? 'achp-hero-icon--earned' : ''}`}>
            <i className="fa fa-trophy" />
          </div>

          <div className="achp-hero-info">
            <div className="achp-hero-top">
              <h2 className="achp-hero-title">{achievement.title}</h2>
              <span className={`achp-category-pill achp-cat-${achievement.category}`}>
                {CATEGORY_LABELS[achievement.category] || achievement.category}
              </span>
              {!achievement.earned && achievement.progress_total > 1 && (
                <span className="achp-progress-count">
                  <i>{Math.min(achievement.user_progress, achievement.progress_total).toLocaleString()} / {achievement.progress_total.toLocaleString()}</i>
                </span>
              )}
              {achievement.earned && (
                <span className="ach-earned-pill">
                  <i className="fa fa-check" /> Earned
                </span>
              )}
            </div>

            <p className="achp-hero-desc">{achievement.description}</p>

            {/* Rewards */}
            {(() => {
              const extras  = ACHIEVEMENT_REWARDS[achievement.key] || [];
              const hasXp   = achievement.reward_xp > 0;
              if (!hasXp && extras.length === 0 && !achievement.reward_description) return null;
              const setInfo  = SET_REWARDS[achievement.key];
              // For set achievements, collapse cosmetics into one row; only show badge items individually
              const rowItems = setInfo ? extras.filter(r => r.type === 'badge') : extras;
              const rewardNodes = [];
              if (hasXp) {
                rewardNodes.push(
                  <span key="xp" className={`achp-reward-item achp-reward-item--xp${achievement.earned ? ' achp-reward-item--xp-earned' : ''}`}>
                    {achievement.earned ? <><i className="fa fa-check" /> </> : '+'}{achievement.reward_xp} XP
                  </span>
                );
              }
              if (setInfo) {
                rewardNodes.push(
                  <div key="set" className="achp-reward-row">
                    <span className="achp-reward-row-label">Set</span>
                    <span className={`ap-pill ap-pill--display achp-reward-cosmetic${setInfo.cls ? ' ' + setInfo.cls : ''}${achievement.earned && !setInfo.cls ? ' ap-pill--earned-generic' : ''}`}>{setInfo.label}</span>
                    <button
                      className="achp-preview-btn"
                      onClick={() => openPreview({ type: 'set', key: achievement.key, label: `${setInfo.label} Set` })}
                      title={`Preview the ${setInfo.label} set on your profile`}
                    >
                      <i className="fa fa-eye" /> Preview
                    </button>
                  </div>
                );
              }
              rowItems.forEach((r, i) => {
                if (r.type === 'badge') {
                  const bdg = BADGE_DISPLAY_MAP[r.label];
                  rewardNodes.push(
                    <div key={`row-${i}`} className="achp-reward-row">
                      <span className="achp-reward-row-label">Badge</span>
                      {bdg
                        ? <span className={`badge ${bdg.cls}`}>{bdg.label}</span>
                        : <span className={`ap-pill ap-pill--display${achievement.earned ? ' ap-pill--earned-generic' : ''}`}>{r.label}</span>
                      }
                      {isPreviewable(r) && (
                        <button className="achp-preview-btn" onClick={() => openPreview(r)} title={`Preview ${r.label} on your profile`}>
                          <i className="fa fa-eye" /> Preview
                        </button>
                      )}
                    </div>
                  );
                } else {
                  const mapKey = `${r.type}:${r.label}`;
                  const disp   = REWARD_DISPLAY_MAP[mapKey];
                  const rowLbl = REWARD_ROW_LABELS[r.type] || r.type;
                  const isThemedCosmetic = disp?.cls && r.type !== 'particles';
                  const isGeneric = !disp?.cls && r.type !== 'particles';
                  rewardNodes.push(
                    <div key={`row-${i}`} className="achp-reward-row">
                      <span className="achp-reward-row-label">{rowLbl}</span>
                      <span className={`ap-pill ap-pill--display${disp?.cls ? ' ' + disp.cls : ' ap-pill--generic'}${isThemedCosmetic ? ' achp-reward-cosmetic' : ''}${r.type === 'particles' ? ' achp-reward-particle' : ''}${achievement.earned && !disp?.cls ? ' ap-pill--earned-generic' : ''}`}>
                        {isGeneric && <i className="fa fa-cog" style={{ marginRight: '0.35em', opacity: 0.75, fontSize: '0.85em' }} />}
                        {disp ? disp.label : r.label}
                      </span>
                      {isPreviewable(r) && (
                        <button className="achp-preview-btn" onClick={() => openPreview(r)} title={`Preview ${r.label} on your profile`}>
                          <i className="fa fa-eye" /> Preview
                        </button>
                      )}
                    </div>
                  );
                }
              });
              if (extras.length === 0 && !hasXp && achievement.reward_description) {
                rewardNodes.push(
                  <div key="desc" className="achp-reward-row">
                    <span className="achp-reward-item">{achievement.reward_description}</span>
                  </div>
                );
              }
              const rewardListWithSeps = rewardNodes.reduce((acc, node, idx) => {
                if (idx > 0) acc.push(<span key={`sep-${idx}`} className="achp-reward-sep">+</span>);
                acc.push(node);
                return acc;
              }, []);
              return (
                <div className={`achp-rewards-wrap${achievement.earned ? ' achp-rewards-wrap--earned' : ''}`}>
                  <button className="achp-rewards-toggle" onClick={() => setRewardsOpen(o => !o)}>
                    <i className="fa fa-gift achp-rewards-icon" />
                    <span className="achp-rewards-label">Rewards ({rewardNodes.length})</span>
                    <i className={`fa fa-chevron-down achp-rewards-chevron${rewardsOpen ? ' achp-rewards-chevron--open' : ''}`} />
                  </button>
                  {rewardsOpen && (
                    <div className="achp-reward-list">
                      {rewardListWithSeps}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Progress bar */}
            {!(achievement.category === 'unobtainable' && hasExpired && !achievement.earned) && (
              <div className="ach-progress-wrap achp-progress-wrap">
                <div className="ach-progress-track">
                  <div className="ach-progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="achp-progress-meta">
                  {achievement.earned && earnedDate && (
                    <span className="achp-earned-date">
                      <i className="fa fa-calendar" /> Earned {earnedDate}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Unobtainable notice */}
            {achievement.category === 'unobtainable' && hasExpired && !achievement.earned && (
              <p className="ach-expired-note">
                <i className="fa fa-lock" /> Unobtainable as of {expiryDate}
              </p>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="achp-stats-row">
          <div className="achp-stat">
            <span className="achp-stat-value">{totalEarners.toLocaleString()}</span>
            <span className="achp-stat-label">Players Earned</span>
          </div>
          {achievement.global_earned_pct != null && (
            <div className="achp-stat">
              <span className="achp-stat-value">{achievement.global_earned_pct.toFixed(1)}%</span>
              <span className="achp-stat-label">Earn Rate</span>
            </div>
          )}
          {achievement.progress_total > 1 && (
            <div className="achp-stat">
              <span className="achp-stat-value">{achievement.progress_total.toLocaleString()}</span>
              <span className="achp-stat-label">Required</span>
            </div>
          )}
          {achievement.reward_xp > 0 && (
            <div className="achp-stat">
              <span className="achp-stat-value">+{achievement.reward_xp}</span>
              <span className="achp-stat-label">XP Reward</span>
            </div>
          )}
        </div>

        {/* Earners */}
        <div className="achp-earners-section">
          <h3 className="achp-earners-heading">
            <i className="fa fa-users" /> Earners {totalEarners > 0 && `(${totalEarners.toLocaleString()})`}
          </h3>

          {pageLoading && <FlameLoader text="Loading..." />}

          {!pageLoading && earners.length === 0 && (
            <p className="ach-empty">No one has earned this yet. Be the first!</p>
          )}

          {!pageLoading && earners.length > 0 && (
            <div className="achp-table-wrap">
            <table className="achp-earners-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Player</th>
                  <th>Date Earned</th>
                </tr>
              </thead>
              <tbody>
                {earners.map(e => (
                  <tr key={e.rank}>
                    <td className="achp-earner-rank">#{e.rank}</td>
                    <td className="achp-earner-user">
                      <Link to={`/user/${e.username}`} className="achp-earner-link">
                        {e.profile_picture ? (
                          <img
                            src={e.profile_picture}
                            alt={e.username}
                            className="achp-earner-avatar"
                            onError={ev => { ev.target.style.display = 'none'; }}
                          />
                        ) : (
                          <span className="achp-earner-avatar-placeholder">
                            <i className="fa fa-user" />
                          </span>
                        )}
                        {e.username}
                      </Link>
                    </td>
                    <td className="achp-earner-date">
                      {new Date(e.earned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="achp-pagination">
              <button
                className="fp-btn fp-btn-outline achp-page-btn"
                disabled={page <= 1}
                onClick={() => handlePage(page - 1)}
              >
                <i className="fa fa-chevron-left" />
              </button>
              <span className="achp-page-info">Page {page} of {totalPages}</span>
              <button
                className="fp-btn fp-btn-outline achp-page-btn"
                disabled={page >= totalPages}
                onClick={() => handlePage(page + 1)}
              >
                <i className="fa fa-chevron-right" />
              </button>
            </div>
          )}
        </div>

      </div>

      {/* ── Preview Modal ── */}
      {previewOpen && (
        <div className="achp-preview-overlay" onClick={closePreview}>
          <div className="achp-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="achp-preview-modal-header">
              <span className="achp-preview-modal-title">
                <i className="fa fa-eye" /> Preview: {previewReward?.label}
              </span>
              <button className="achp-preview-close" onClick={closePreview} aria-label="Close preview">
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="achp-preview-modal-body">
              {!authData?.user?.id ? (
                <p className="achp-preview-no-auth">Log in to preview rewards on your profile.</p>
              ) : previewLoading ? (
                <FlameLoader text="Loading your profile..." />
              ) : previewError ? (
                <p className="error-text">{previewError}</p>
              ) : previewUser ? (
                previewReward?.type === 'particles' ? (
                  /* ── Particle scene preview ── */
                  (() => {
                    const isFire  = previewReward.label === 'Fire';
                    const isStars = previewReward.label === 'Stars';
                    const SHOOTING_STAR_STYLE = {
                      position: 'absolute',
                      width: 110,
                      height: 1.5,
                      borderRadius: 2,
                      background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.95) 55%, rgba(180,220,255,0.55))',
                      pointerEvents: 'none',
                      animation: 'shoot-star 10s linear infinite',
                    };
                    return (
                      <>
                      <div className={`achp-particle-scene achp-particle-scene--${isFire ? 'fire' : 'stars'}`}>
                        {isStars && (
                          <>
                            {/* Nebulas */}
                            <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 55% 45% at 25% 35%, rgba(90,0,180,0.28) 0%, rgba(50,0,120,0.1) 45%, transparent 68%)' }} className="space-nebula-purple" />
                            <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 50% 50% at 75% 65%, rgba(0,120,220,0.22) 0%, rgba(0,60,160,0.08) 45%, transparent 68%)' }} className="space-nebula-blue" />
                            {/* Stars particles fill scene */}
                            <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none' }}>
                              <ParticlesBg preset="stars" />
                            </div>
                            {/* Shooting stars — use contained keyframe so they cross the scene */}
                            <div style={{ ...SHOOTING_STAR_STYLE, top: '10%', left: '-110px', zIndex: 12, animationDelay: '0s',   animationDuration: '9s',  animation: 'shoot-star-scene 9s linear 0s infinite' }} />
                            <div style={{ ...SHOOTING_STAR_STYLE, top: '28%', left: '-110px', zIndex: 12, animationDelay: '4.5s', animationDuration: '9s',  animation: 'shoot-star-scene 9s linear 4.5s infinite' }} />
                            {/* Vignettes */}
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '25%', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to bottom, rgba(0,0,5,0.85) 0%, transparent 100%)' }} />
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '18%', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to top, rgba(0,0,5,0.85) 0%, transparent 100%)' }} />
                            <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '8%', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to right, rgba(0,0,8,0.78) 0%, transparent 100%)' }} />
                            <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '8%', zIndex: 2, pointerEvents: 'none', background: 'linear-gradient(to left, rgba(0,0,8,0.78) 0%, transparent 100%)' }} />
                          </>
                        )}
                        {isFire && (
                          <>
                            {/* Fire dark background — beneath card */}
                            <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 80% 40% at 50% 115%, rgba(160,20,0,0.3) 0%, transparent 70%)' }} />
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '30%', zIndex: 0, pointerEvents: 'none', background: 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, transparent 100%)' }} />
                            {/* Fire particles fill the whole scene — zIndex 11 so they appear in front of the card */}
                            <div style={{ position: 'absolute', inset: 0, zIndex: 11, pointerEvents: 'none' }}>
                              <ParticlesBg preset="fire" />
                            </div>
                          </>
                        )}
                        <div style={{ position: 'relative', zIndex: 10, padding: '1.5rem 1.25rem' }}>
                          <ProfileMiniCard
                            user={previewUser}
                            reward={previewReward}
                            setPreviewKey={setPreviewKey}
                            setToggles={setToggles}
                            customPreview={customPreview}
                            particlesPreset={null}
                          />
                        </div>
                        {isFire && (
                          <>
                            {/* Orange glow + vignettes — ON TOP of particles */}
                            <div className="fire-bottom-glow" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%', zIndex: 12, pointerEvents: 'none', background: ['radial-gradient(ellipse 75% 90% at 50% 100%, rgba(255,100,0,0.42) 0%, rgba(200,40,0,0.18) 45%, transparent 72%)', 'linear-gradient(to top, rgba(3,0,0,0.97) 0%, rgba(3,0,0,0.65) 55%, transparent 100%)'].join(', '), animation: 'fire-bottom-pulse 3s ease-in-out infinite' }} />
                            <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '10%', zIndex: 12, pointerEvents: 'none', background: 'linear-gradient(to right, rgba(0,0,0,0.82) 0%, transparent 100%)' }} />
                            <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '10%', zIndex: 12, pointerEvents: 'none', background: 'linear-gradient(to left, rgba(0,0,0,0.82) 0%, transparent 100%)' }} />
                          </>
                        )}
                      </div>
                      <p className="achp-preview-hint" style={{ marginTop: '0.6rem', marginBottom: 0 }}>
                        <i className="fa fa-info-circle" style={{ marginRight: '0.35em', opacity: 0.7 }} />
                        This effect covers your <strong>entire profile</strong>, not just the header card.
                      </p>
                      </>
                    );
                  })()
                ) : (
                <>
                  {setPreviewKey && SET_PREVIEW_CONFIG[setPreviewKey] && (
                    <div className="achp-set-toggles">
                      {Object.entries(SET_PREVIEW_CONFIG[setPreviewKey]).map(([key, cfg]) => {
                        const icons = { card: 'fa-th-large', name: 'fa-font', banner: 'fa-picture-o', border: 'fa-circle-o' };
                        return (
                          <button
                            key={key}
                            className={`achp-set-toggle${setToggles[key] ? ' achp-set-toggle--on' : ''}`}
                            onClick={() => setSetToggles(prev => ({ ...prev, [key]: !prev[key] }))}
                            title={setToggles[key] ? `Hide ${cfg.label}` : `Show ${cfg.label}`}
                          >
                            <i className={`fa ${icons[key] || 'fa-square'}`} />
                            {cfg.label}
                            <i className={`fa ${setToggles[key] ? 'fa-eye' : 'fa-eye-slash'} achp-set-toggle-vis`} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {(() => {
                    if (!previewReward) return null;
                    const isColorReward = ['Card Color', 'Border Color', 'Name Color'].includes(previewReward.label);
                    const isAnimReward  = ['Card Animations', 'Border Animations', 'Name Animations'].includes(previewReward.label);
                    const isBannerReward = previewReward.label === 'Custom Banner';
                    if (!isColorReward && !isAnimReward && !isBannerReward) return null;
                    return (
                      <div className="achp-custom-controls">
                        <div className="achp-color-row">
                          <label className="achp-color-label">
                            {isAnimReward || isBannerReward ? 'Color 1' : 'Color'}
                            <input
                              type="color"
                              className="achp-color-input"
                              value={customPreview.color1}
                              onChange={e => setCustomPreview(p => ({ ...p, color1: e.target.value }))}
                            />
                          </label>
                          {(isAnimReward || isBannerReward) && (
                            <label className="achp-color-label">
                              Color 2
                              <input
                                type="color"
                                className="achp-color-input"
                                value={customPreview.color2}
                                onChange={e => setCustomPreview(p => ({ ...p, color2: e.target.value }))}
                              />
                            </label>
                          )}
                          {isAnimReward && (
                            <div className="achp-anim-btns">
                              <button
                                className={`achp-set-toggle${customPreview.animMode === 'pulse' ? ' achp-set-toggle--on' : ''}`}
                                onClick={() => setCustomPreview(p => ({ ...p, animMode: 'pulse' }))}
                              >Pulse</button>
                              <button
                                className={`achp-set-toggle${customPreview.animMode === 'gradient' ? ' achp-set-toggle--on' : ''}`}
                                onClick={() => setCustomPreview(p => ({ ...p, animMode: 'gradient' }))}
                              >Gradient</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  <ProfileMiniCard user={previewUser} reward={previewReward} setPreviewKey={setPreviewKey} setToggles={setToggles} customPreview={customPreview} />
                  <p className="achp-preview-hint" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                    {setPreviewKey
                      ? <>Your profile card with the <strong>{previewReward?.label}</strong> applied. Toggle elements above to show or hide each part.</>
                      : ['Card Color', 'Border Color', 'Name Color'].includes(previewReward?.label)
                      ? <>Pick a color to preview <strong>{previewReward?.label}</strong> on your profile.</>
                      : ['Card Animations', 'Border Animations', 'Name Animations'].includes(previewReward?.label)
                      ? <>Pick colors and animation style for <strong>{previewReward?.label}</strong>.</>
                      : previewReward?.label === 'Custom Banner'
                      ? <>Pick two colors to preview your <strong>Custom Banner</strong>.</>
                      : <>Your profile card with <strong>{previewReward?.label}</strong> applied.
                          {previewReward?.type === 'tagline' && !previewUser.customization?.tagline && (
                            <> Tagline shown as <em>"Example Tagline."</em></>
                          )}
                        </>
                    }
                  </p>
                </>
                )
              ) : null}
            </div>
          </div>
        </div>
      )}

    </BaseLayout>
  );
};

export default AchievementPage;
